/**
 * Modul „Warenwirtschaft“ (Registry-Schlüssel `lager`, Pfad `/lager`) — NEO-Bestandssync #863 (ADR 007).
 * Lese-API `/api/warenwirtschaft/v1/*` (Team-Session, Cerbos `warenwirtschaft` lesen/verwalten).
 * Inbox `/api/integration/inbox/neo/warenwirtschaft` + Cursor `/api/integration/cursor/neo/warenwirtschaft`
 * (Dienstkonto-Token, tenantgebunden) — Vertrag für n8n `wf_portal_warenwirtschaft_neo_1` (Schritt B).
 * #899b (GO Matthias 05.11.2026, SPEC docs/SPEC-WARENWIRTSCHAFT.md v2): Artikelliste = komplettes NEO-Sortiment (Bestandsliste
 * `withNullBestand=true`, inkl. Bestand 0); die Regel „nur ≠ 0“ (04.11.) ist aufgehoben. Filialspalten dynamisch (nur Filialen mit
 * realem Bestand), Altlasten/Sonderfiliale als Hinweis. Shopify-Spiegel je SKU `NEO-<artikelNr>` (`wf_portal_warenwirtschaft_shopify_1`).
 */

export const WAWI_API = '/api/warenwirtschaft/v1';
export const WAWI_INBOX_PFAD = '/api/integration/inbox/neo/warenwirtschaft';
export const WAWI_CURSOR_PFAD = '/api/integration/cursor/neo/warenwirtschaft';
export const WAWI_STREAM = 'neo_warenwirtschaft';
/** höchstens 500 Einträge je Inbox-Seite (NEO-Seitengröße `anzahl=500`) */
export const WAWI_BATCH_MAX = 500;
export const WAWI_SONDERFILIALE = 999;
/** #899b Filiale, gegen die Shopify gespiegelt wird (WF6 `FIL_INCLUDE={1}`) — Basis der Abweichung NEO − Shopify */
export const WAWI_SHOP_FILIALE = 1;
/**
 * #899b eigene Spalte nur für Filialen (außer 999) mit mindestens diesem Anteil an allen Bestandszeilen > 0; sonst Hinweis
 * „Altlasten“ (Befund #899: F1 99,8 %, Hirmer 1 Zeile > 0 + 12 negativ, Alter Peter 0 Zeilen)
 */
export const WAWI_HAUPTFILIALE_ANTEIL = 0.05;
/** #899b Shopify-Stream im Sync-Zustand; Läufe `shop-*` */
export const WAWI_SHOP_STREAM = 'shopify_bestand';
export const WAWI_SHOP_INBOX_PFAD = '/api/integration/inbox/shopify/warenwirtschaft';
export const WAWI_SHOP_CURSOR_PFAD = '/api/integration/cursor/shopify/warenwirtschaft';
/** #899b Export höchstens so viele Zeilen (Sortiment ≈ 7.000–10.000) */
export const WAWI_EXPORT_MAX = 20000;
/** Stand gilt als veraltet, wenn der letzte veröffentlichte Snapshot älter ist (Nachtlauf 01:00 + Reserve) */
export const WAWI_VERALTET_STUNDEN = 36;

/** alle (Standard #899b) · gt0/lt0/null = Bestand der Shop-Filiale · ungleich0/kein/negativ wie #863 (über alle Filialen) */
export const WAWI_BESTAND_FILTER = ['alle', 'gt0', 'lt0', 'null', 'ungleich0', 'kein', 'negativ'] as const;
/** #899b Shopify-Status je Artikel: aktiv (ACTIVE) · entwurf (DRAFT) · archiviert · fehlt (keine Variante mit SKU NEO-<nr>) */
export const WAWI_SHOP_STATUS = ['aktiv', 'entwurf', 'archiviert', 'fehlt'] as const;
export type WawiShopStatus = (typeof WAWI_SHOP_STATUS)[number];
/**
 * #899b Prüflisten (Kacheln + Drilldown `pruefliste=`):
 * neo_ohne_shop = Bestand Shop-Filiale > 0, keine Shopify-Variante · shop_ohne_neo = im Shop verfügbar (ACTIVE und Bestand > 0 oder
 * „weiter verkaufen“), aber Bestand Shop-Filiale ≤ 0 · minus = Bestand Shop-Filiale < 0 · abweichung = NEO − Shopify ≠ 0
 */
export const WAWI_PRUEFLISTEN = ['neo_ohne_shop', 'shop_ohne_neo', 'minus', 'abweichung'] as const;
export type WawiPruefliste = (typeof WAWI_PRUEFLISTEN)[number];
export const WAWI_SORTIERUNG = ['nr', 'bezeichnung', 'marke', 'bestand', 'shop', 'abweichung', 'geaendert'] as const;
export type WawiSortierung = (typeof WAWI_SORTIERUNG)[number];
export type WawiBestandFilter = (typeof WAWI_BESTAND_FILTER)[number];

/* ---------------------------------------------------------------- Inbox (n8n → Portal) */

export const WAWI_INBOX_KINDS = ['stock', 'articles', 'nachabruf', 'complete', 'fehler'] as const;
export type WawiInboxKind = (typeof WAWI_INBOX_KINDS)[number];

/** Zeile der NEO-Standard-Bestandsliste (`/bewegungsdaten/artikelbestaende`), Felder unverändert */
export interface NeoBestandZeile {
  artikelNr: number;
  filialeNr: number;
  bestand: number;
  avisiert?: number | null;
  reserviert?: number | null;
  meldemenge?: number | null;
  imWarenausgangBereitgestellt?: number | null;
  imWarenausgangDerBestellfilialeBereitgestellt?: number | null;
}

/** Artikel aus `/stammdaten/artikel/{nr}` (Einzelabruf je Bestandsartikel, #899b2; Projektion PREISE,ZEITPUNKTE,EINSTELLUNGEN) */
export interface NeoArtikelSatz {
  artikelNr: number;
  deltaVersion: number;
  bezeichnung?: string | null;
  artikelStatus?: string | null;
  artikeltyp?: string | null;
  gtin?: string | null;
  herstellerArtikelNr?: string | null;
  referenzNr?: string | null;
  preise?: { ekPreis?: number | null; vkPreis?: number | null; uvpPreis?: number | null; sperre?: boolean | null } | null;
  einstellungen?: {
    keineBestandsfuehrung?: boolean | null;
    automatischeBestellungSperre?: boolean | null;
    filialtauschSperre?: boolean | null;
    meldemengeAutomatikSperre?: boolean | null;
  } | null;
  sortimentzuordnung?: {
    oberwarengruppeNummer?: string | null;
    oberwarengruppe?: string | null;
    warengruppeNummer?: string | null;
    warengruppe?: string | null;
    kategorie?: string | null;
    markeNummer?: number | null;
    marke?: string | null;
    submarkeNummer?: number | null;
    submarke?: string | null;
    linieNummer?: number | null;
    linie?: string | null;
    lieferant?: string | null;
    folgeArtikelGtin?: string | null;
  } | null;
  /** NEO-Datumsformat dd.MM.yyyy */
  zeitpunkte?: {
    erstlistung?: string | null;
    exDatum?: string | null;
    bestellbarVon?: string | null;
    bestellbarBis?: string | null;
    lieferbarAb?: string | null;
    nichtLieferbarSeit?: string | null;
    nichtMehrBestellenAb?: string | null;
  } | null;
}

/**
 * `POST /api/integration/inbox/neo/warenwirtschaft` — eine Seite eines Laufs. Idempotent je (runId, kind, seite):
 * gleicher Inhalt → `duplicate` mit derselben Quittung, abweichender Inhalt → 409.
 * - stock: Seite der Standard-Bestandsliste (seite 0..n-1), wird bis `complete` vorgemerkt
 * - articles: Stammdaten-Seite (seite 0..), vollständige NEO-Seite inkl. später verworfener IDs; Portal übernimmt nur
 *   geführte Artikel und Kandidaten (Bestand ≠ 0 im vorgemerkten Snapshot) und schreibt den Cursor = max(deltaVersion)
 *   aller Zeilen fort (ohne +1; leere Seite belässt ihn)
 * - nachabruf: Einzelabrufe neuer Bestands-IDs ohne Stammsatz (U); Cursor bleibt
 * - complete: prüft alle Bestandsseiten + gelöste Kandidaten und veröffentlicht den Snapshot atomar
 * - fehler: Lauf meldet Abbruch (Lease frei, Fehler sichtbar; kein Teil-Snapshot)
 */
export interface WawiInboxRequest {
  runId: string;
  kind: WawiInboxKind;
  seite: number;
  /** Abrufzeit der Bestandsliste (stock, Pflicht); bei complete optional zur Kontrolle */
  observedAt?: string;
  /** articles: Cursor, mit dem die Seite abgerufen wurde (muss dem bestätigten Portal-Cursor entsprechen) */
  cursorBefore?: number | null;
  /** stock: NeoBestandZeile[]; articles/nachabruf: NeoArtikelSatz[]; complete/fehler: [] */
  items: unknown[];
  /** complete: Anzahl Bestandsseiten und -zeilen des Snapshots */
  bestandSeiten?: number;
  bestandZeilen?: number;
  /** fehler: kurze Meldung (ohne Tokens) */
  meldung?: string;
}

export type WawiQuittungStatus = 'gespeichert' | 'duplicate' | 'veroeffentlicht' | 'fehler_erfasst';

/** Quittung = persistierte Verarbeitung; erst danach darf n8n den NEO-Cursor fortschreiben */
export interface WawiInboxQuittung {
  status: WawiQuittungStatus;
  runId: string;
  kind: WawiInboxKind;
  seite: number;
  anzahl: number;
  /** articles/nachabruf: übernommen = geführt oder Kandidat, verworfen = bewusst nicht geführt */
  uebernommen?: number;
  verworfen?: number;
  /** stock: Zeilen mit Bestand ≠ 0 (Kandidaten) */
  kandidaten?: number;
  /** bestätigter Stamm-Cursor nach dieser Seite */
  artikelCursor: number | null;
  /** complete */
  snapshotId?: string;
  artikelMitBestand?: number;
  nullgesetzt?: number;
  keinBestandNeu?: number;
  /** #899b complete: Platzhalter für Artikel ohne Stammsatz (Snapshot wird trotzdem veröffentlicht) */
  stammFehlt?: number;
  /** #899b complete: Nummern der neuen Platzhalter (#899b2: höchstens 20.000) — n8n ruft sie einzeln nach (`nachabruf`) */
  offen?: number[];
  quittiertAt: string;
}

/** Fehler der Inbox: 400 Schema, 401 Token, 409 Konflikt/Lease/Cursor/veraltet/Kandidaten offen, 413 > 500, 503 nicht konfiguriert */
export interface WawiInboxFehler {
  statusCode: number;
  code: string;
  message: string;
  /** complete mit offenen Kandidaten: Artikelnummern ohne Stammsatz */
  offen?: number[];
}

/** `GET /api/integration/cursor/neo/warenwirtschaft` */
export interface WawiCursor {
  stream: string;
  /** nächste Stammseite mit `version=<artikelCursor>` (exklusiv, ohne +1); null = Bootstrap ab version=0 */
  artikelCursor: number | null;
  /** Abrufzeit des zuletzt veröffentlichten Bestands-Snapshots */
  snapshotAt: string | null;
  lastSuccessAt: string | null;
  /** unvollstaendig = Abschluss abgelehnt (Seiten/Kandidaten fehlen), alter Snapshot bleibt aktiv (Review 221) */
  phase: 'bereit' | 'laeuft' | 'unvollstaendig' | 'fehler';
  runId: string | null;
  lockedUntil: string | null;
  /** aufeinanderfolgende abgelehnte Abschlüsse; 0 nach Erfolg */
  fehlerZaehler: number;
  /** Fortschritt des aktuellen Laufs; zurKlaerung = offene Kandidaten ohne Stammsatz aus dem letzten abgelehnten Abschluss */
  checkpoint: { bestandSeiten?: number; artikelSeite?: number; zurKlaerung?: number[] };
  /** #899b2 Snapshot veröffentlicht und alle Bestandsartikel haben einen Stammsatz (keine Platzhalter) — ohne Bindung an einen Stamm-Strom */
  stammVollstaendig: boolean;
  /** #899b Artikel im Snapshot ohne Stammsatz (Platzhalter, werden per Einzelabruf `nachabruf` gefüllt) */
  stammFehlt: number;
  /** #899b2 ArtikelNr aller Platzhalter (aufsteigend, höchstens 20.000) — einzige Quelle für den gezielten Stamm-Einzelabruf in n8n */
  stammOffen: number[];
}

/* ---------------------------------------------------------------- Lese-API (Team) */

export interface WawiFilialBestand {
  filialNr: number;
  bestand: number;
  sonderfiliale: boolean;
}

export interface WawiArtikelKurz {
  artikelNr: number;
  bezeichnung: string | null;
  marke: string | null;
  oberNr: string | null;
  oberName: string | null;
  wgNr: string | null;
  wgName: string | null;
  bestaende: WawiFilialBestand[];
  /** in keiner Filiale Bestand ≠ 0 (Nullübergang) */
  keinBestand: boolean;
  keinBestandSeit: string | null;
  negativ: boolean;
  /** #899b */
  gtin: string | null;
  vkPreis: number | null;
  artikelStatus: string | null;
  /** Stammsatz folgt (Platzhalter aus dem Bestands-Snapshot) */
  stammFehlt: boolean;
  /** Σ reserviert über alle Filialen */
  reserviert: number;
  /** Shopify: Σ inventoryQuantity der vorhandenen Varianten mit SKU NEO-<nr>; null = nicht im Shop */
  shopBestand: number | null;
  shopStatus: WawiShopStatus;
  /** Bestand Shop-Filiale (WAWI_SHOP_FILIALE) − Shopify; null, wenn eine Seite fehlt */
  abweichung: number | null;
  geaendertAt: string;
}

export interface WawiStand {
  snapshotAt: string | null;
  lastSuccessAt: string | null;
  veraltet: boolean;
}

/**
 * `GET /api/warenwirtschaft/v1/artikel?suche=&filiale=&ober=&wg=&zuordnung=ohne&bestand=&marke=&status=&shop=&pruefliste=&abweichung=ja
 *   &sort=nr|bezeichnung|marke|bestand|shop|abweichung|geaendert&richtung=auf|ab&seite=&groesse=` — CSV: `GET …/artikel.csv` (gleiche Filter)
 */
export interface WawiArtikelListe {
  eintraege: WawiArtikelKurz[];
  gesamt: number;
  seite: number;
  groesse: number;
  /** #899b Filialen mit eigener Spalte (dynamisch, nur realer Bestand), aufsteigend */
  filialen: number[];
  stand: WawiStand;
}

export interface WawiFilialBestandDetail extends WawiFilialBestand {
  avisiert: number;
  reserviert: number;
  meldemenge: number;
  ausgang: number;
  ausgangBestellfiliale: number;
  observedAt: string;
}

/** #899b Filiale mit eigener Spalte; Name aus NEO nicht abrufbar → CRM-Filialstamm (`kunden.filiale`, nameQuelle crm) */
export interface WawiFiliale {
  nr: number;
  name: string | null;
  nameQuelle: 'crm' | null;
  zeilen: number;
  positiv: number;
  negativ: number;
  summe: number;
}
/** #899b Prüfliste: Kachel mit Zähler (Drilldown über `artikel?pruefliste=<key>`) */
export interface WawiPruefKachel { key: WawiPruefliste; label: string; anzahl: number }
/** `GET /api/warenwirtschaft/v1/uebersicht` (#899b) */
export interface WawiUebersicht {
  /** Artikel im Sortiment (NEO) gesamt / davon Platzhalter ohne Stammsatz */
  artikel: number;
  stammFehlt: number;
  /** Filialen mit eigener Spalte (Anteil ≥ WAWI_HAUPTFILIALE_ANTEIL an allen Zeilen > 0) */
  filialen: WawiFiliale[];
  /** Altlasten (z. B. Hirmer) und Sonderfiliale 999 — Hinweisblock, keine Hauptspalte */
  hinweisFilialen: (WawiFiliale & { art: 'altlast' | 'sonder' })[];
  pruefung: WawiPruefKachel[];
  shop: { varianten: number; mitNeoSku: number; ohneNeoSku: number; lastSuccessAt: string | null; phase: string };
  stand: WawiStand;
}
/* ---------------------------------------------------------------- Shopify-Inbox (n8n → Portal, #899b) */
export interface ShopifyVariante {
  variantId: string;
  productId: string;
  sku: string | null;
  titel: string;
  produktStatus: 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
  inventoryQuantity: number | null;
  inventoryPolicy: 'DENY' | 'CONTINUE' | null;
  tracked: boolean | null;
}
/** `POST /api/integration/inbox/shopify/warenwirtschaft` — Seiten 0..n-1 (kind varianten, ≤ 500), dann complete (varianten = Summe) */
export interface WawiShopInboxRequest {
  runId: string;
  kind: 'varianten' | 'complete' | 'fehler';
  seite: number;
  items: ShopifyVariante[];
  varianten?: number;
  meldung?: string;
}
export interface WawiShopQuittung {
  status: 'gespeichert' | 'duplicate' | 'veroeffentlicht' | 'fehler_erfasst';
  runId: string;
  kind: WawiShopInboxRequest['kind'];
  seite: number;
  anzahl: number;
  /** complete: Varianten früherer Läufe, die jetzt fehlen (vorhanden=false) */
  entfallen?: number;
  quittiertAt: string;
}
/** `GET /api/integration/cursor/shopify/warenwirtschaft` */
export interface WawiShopCursor {
  stream: typeof WAWI_SHOP_STREAM;
  phase: 'bereit' | 'laeuft' | 'fehler';
  runId: string | null;
  lockedUntil: string | null;
  lastSuccessAt: string | null;
}

/** `GET /api/warenwirtschaft/v1/artikel/:nr` */
export interface WawiArtikel extends Omit<WawiArtikelKurz, 'bestaende'> {
  deltaVersion: number;
  artikelStatus: string | null;
  artikeltyp: string | null;
  gtin: string | null;
  herstellerArtikelNr: string | null;
  referenzNr: string | null;
  kategorie: string | null;
  submarke: string | null;
  linie: string | null;
  lieferant: string | null;
  ekPreis: number | null;
  vkPreis: number | null;
  uvpPreis: number | null;
  preisSperre: boolean | null;
  keineBestandsfuehrung: boolean | null;
  erstlistung: string | null;
  exDatum: string | null;
  firstSeenAt: string;
  sourceSeenAt: string;
  bestaende: WawiFilialBestandDetail[];
  stand: WawiStand;
}

export interface WawiFilialAggregat {
  filialNr: number;
  summe: number;
  positiv: number;
  negativ: number;
  /** Artikel mit Zeile in dieser Filiale, aber Bestand 0 */
  null: number;
}

export interface WawiWarengruppe {
  wgNr: string | null;
  wgName: string | null;
  artikel: number;
  keinBestand: number;
  filialen: WawiFilialAggregat[];
}

export interface WawiOberwarengruppe {
  /** null = „Nicht zugeordnet“ */
  oberNr: string | null;
  oberName: string | null;
  artikel: number;
  keinBestand: number;
  filialen: WawiFilialAggregat[];
  gruppen: WawiWarengruppe[];
}

/** `GET /api/warenwirtschaft/v1/warengruppen` */
export interface WawiWarengruppen {
  baum: WawiOberwarengruppe[];
  filialen: number[];
  stand: WawiStand;
}

/** `GET /api/warenwirtschaft/v1/sync` (Cerbos warenwirtschaft verwalten) */
export interface WawiSync extends WawiCursor {
  lastError: string | null;
  lastErrorAt: string | null;
  snapshotId: string | null;
  artikel: number;
  keinBestand: number;
  veraltet: boolean;
}
